import React, { useState, useEffect, useContext, useRef } from 'react';
import { AppContext } from '../../../util/AppContext';
import PropType from 'prop-types';
import { getConfigValue } from '../../../util/getConfigValue';
import { Chart as ChartJS, Title, Tooltip, Legend, CategoryScale, LinearScale, BarElement } from 'chart.js';
import { Bar } from 'react-chartjs-2';
import { getUtlizationLeaderboardData } from '../../../API/UtilizationReportingDashboard';
import { constructChartData, downloadDivChart, getTimePeriodLabel, utilizationReportConstants, setChartXAxes, getTimePeriodTile, baseHorizontalBarChartOptions } from './utlizationChart';
import { useErrorBoundary } from "react-error-boundary";
import ReportInfoTooltip from "../ReportInfoTooltip";
import Button from 'react-bootstrap/Button';
import ChartDataLabels from "chartjs-plugin-datalabels";

UtilizationLeaderboardChart.propTypes = {
    filterState: PropType.object,
    colorRegistry: PropType.object
}

function UtilizationLeaderboardChart(props) {
    const {
        filterState,
        colorRegistry
    } = props;

    const { showBoundary } = useErrorBoundary();
    const [appState] = useContext(AppContext);
    const chartRef = useRef(undefined);
    const [data, setData] = useState({});
    const [chartData, setChartData] = useState({});
    const [isLoading, setIsLoading] = useState(null);
    const [isCollapsed, setIsCollapsed] = useState(false);
    const [activeButton, setActiveButton] = useState(utilizationReportConstants.current);
    const maxUsersToDisplay = getConfigValue(appState?.config?.UtilizationDashboard?.Charts?.UtilizationLeaderboard?.Filters?.MaxUsers, appState)?.Value || 10;
    const tooltipTarget = useRef(null);
    const [maxXValue, setMaxXValue] = useState(null);
    const [xAxesStepIncrementValue, setXAxesStepIncrementValue] = useState(1);
    const chartEnabled = JSON.parse(getConfigValue(appState?.config?.UtilizationDashboard?.Charts?.UtilizationLeaderboard?.Enabled, appState)?.Value === 'true') || false;
    const noDataMessage = getConfigValue(appState?.config?.UtilizationDashboard?.Charts?.NoDataMessage, appState)?.Value;

    ChartJS.register(Title, Tooltip, Legend, CategoryScale, LinearScale, BarElement, ChartDataLabels);

    const getData = async () => {
        if (filterState.timePeriodId == undefined) {
            return;
        }

        setIsLoading(true);
        await getUtlizationLeaderboardData({
            "timeFilter": filterState.timePeriodId,
            "includePriorPeriod": true,
            "maxUsers": maxUsersToDisplay
        })
            .then((responseJson) => {
                setData(responseJson);
                setChartData({ labels: responseJson.currentPeriod.labels, datasets: constructChartData(responseJson.currentPeriod.dataSets, colorRegistry, appState) });
                setChartXAxes(responseJson.currentPeriod.dataSets, setXAxesStepIncrementValue, setMaxXValue);
            })
            .catch((err) => {
                showBoundary(err);
            })
            .finally(() => {
                setIsLoading(false);
            });
    }

    const toggleChartTab = (activeTab, selectedData) => {
        setChartData({ labels: selectedData.labels, datasets: constructChartData(selectedData.dataSets, colorRegistry, appState) });
        setActiveButton(activeTab);
        setChartXAxes(selectedData.dataSets, setXAxesStepIncrementValue, setMaxXValue);
    }

    ChartJS.register({
        id: 'noDataChartPlugin',
        beforeDraw: (chart) => {
            const centerConfig = chart.options.plugins.noDataChartPlugin;
            const hasData = chart?.data?.datasets?.some(dataset => dataset?.data?.length > 0);
            const messageText = centerConfig.text || "";

            if (!hasData) {
                const { ctx, chartArea } = chart;
                const { top, bottom, left, right } = chartArea;
                const centerX = (left + right) / 2;
                const centeryY = (top + bottom) / 2;

                ctx.save();
                ctx.textAlighn = 'center';
                ctx.textBaseline = 'middle';
                ctx.fillStyle = 'gray';
                ctx.font = '16px sans-serif';
                ctx.fillText(messageText, centerX, centeryY);
                ctx.restore();
            }
        }
    });

    const chartOptions = {
        indexAxis: 'y',
        responsive: true,
        scales: {
            y: {
                ...baseHorizontalBarChartOptions.scales.y,
          
            },
            x: {
                max: maxXValue,
                ...baseHorizontalBarChartOptions.scales.x,
                ticks: {
                    beginAtZero: true,
                    stepSize: xAxesStepIncrementValue,
                    ...baseHorizontalBarChartOptions.scales.x.ticks
                }
            }
        },
        layout: baseHorizontalBarChartOptions.layout,
        plugins: {
            noDataChartPlugin: {
                text: noDataMessage
            },
            datalabels: baseHorizontalBarChartOptions.datalabels,
            legend: baseHorizontalBarChartOptions.legend,
            title: {
                ...baseHorizontalBarChartOptions.title,
                text: getTimePeriodLabel(filterState.timePeriodId, activeButton, false),
            },
            tooltip: baseHorizontalBarChartOptions.tooltip,
        },
        resizeDelay:  200,
    };

    useEffect(() => {
        if (chartEnabled && appState?.config && !isLoading) {
            getData();
        }
    }, [filterState.timePeriodId]);

    let content;
    if (isLoading === null || isLoading) {
        content = (<div className='p-5'>{getConfigValue(appState.config.Common?.Loading, appState)?.Value}</div>);
    }
    else {
        content = (
            <>
                <div id="utilizationLeaderboard-ChartsArea" className='chart-canvas-wrapper'>
                    <Bar className={`chart ${isCollapsed ? 'collapsed' : ''}`} ref={chartRef} options={chartOptions} data={chartData} />
                </div>
                <div className='reportButtons-container'>
                    <Button
                        className='reportTextButton'
                        onClick={() => downloadDivChart('utilizationLeaderboard-ChartsArea', getConfigValue(appState?.config?.UtilizationDashboard?.Charts?.UtilizationLeaderboard?.Title, appState)?.Value || null)}
                    >
                        {getConfigValue(appState?.config?.UtilizationDashboard?.Charts?.Buttons?.Download?.Text, appState)?.Value || null} <i className={getConfigValue(appState?.config?.UtilizationDashboard?.Charts?.Buttons?.Download?.Icon, appState)?.Value || null}></i>
                    </Button>
                </div>
                <div className='reportTabButton-container'>
                    <Button
                        className={`tab-button ${activeButton === utilizationReportConstants.current ? "active" : "inactive"}`}
                        onClick={() => { toggleChartTab(utilizationReportConstants.current, data.currentPeriod); }}
                    >
                        {getConfigValue(appState?.config?.UtilizationDashboard?.Charts?.Buttons?.CurrentSelection?.Text, appState)?.Value || null} ({getTimePeriodTile(filterState.timePeriodId, filterState.timePeriodTitle, false)})
                    </Button>
                    <Button
                        className={`tab-button ${activeButton === utilizationReportConstants.prior ? "active" : "inactive"}`}
                        onClick={() => { toggleChartTab(utilizationReportConstants.prior, data.priorPeriod); }}
                    >
                        {getConfigValue(appState?.config?.UtilizationDashboard?.Charts?.Buttons?.PriorTimePeriod?.Text, appState)?.Value || null}
                    </Button>
                </div>
            </>
        );
    }

    return (
        <>
            {chartEnabled &&
                <div className='chart-panel'>
                    <div className='chart-header'>
                        <Button className={`chart-header-button ${isCollapsed ? 'collapsed' : ''}`} onClick={() => { setIsCollapsed(!isCollapsed) }}>
                            {getConfigValue(appState?.config?.UtilizationDashboard?.Charts?.UtilizationLeaderboard?.Title, appState)?.Value || null}
                            <ReportInfoTooltip ref={tooltipTarget} text={getConfigValue(appState?.config?.UtilizationDashboard?.Charts?.UtilizationLeaderboard?.ToolTip, appState)?.Value} />
                        </Button>
                    </div>
                    <div className={`chart-container ${isCollapsed ? 'collapsed' : ''}`}>
                        {content}
                    </div>
                </div>
            }
        </>
    );
}

export default UtilizationLeaderboardChart;